import clsx from 'clsx';
import {
  BarChart3,
  Activity,
  AppWindow,
  Award,
  Bell,
  BellRing,
  Briefcase,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  ChevronsUpDown,
  ClipboardList,
  Clock,
  Coins,
  Crown,
  Columns3,
  Download,
  FileDiff,
  Gauge,
  Globe,
  HardDrive,
  House,
  KeyRound,
  Languages,
  ListOrdered,
  ListTodo,
  LogOut,
  Mail,
  Megaphone,
  Menu,
  MessageCircle,
  MessageSquareWarning,
  Monitor,
  MonitorOff,
  Moon,
  Phone,
  ScrollText,
  Search,
  Server,
  Settings,
  ShieldAlert,
  Sparkles,
  Siren,
  Smartphone,
  Sticker,
  StickyNote,
  SlidersHorizontal,
  Sun,
  Ticket,
  Timer,
  TreePalm,
  Users,
  Volume2,
  VolumeX,
  Wallet,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { relativeTime } from '../lib/format';
import { useWaitingBreakRequests } from '../lib/break-requests';
import { useWaitingOvertime } from '../lib/overtime';
import { useWaitingRemoteWork } from '../lib/remote-work';
import { useWaitingMeetings } from '../lib/meetings';
import { useWaitingShortDays } from '../lib/shortfall';
import { useNotificationChime, useNotificationSound } from '../lib/notification-sound';
import {
  closeShown,
  promptPutOff,
  putOffPrompt,
  turnOff,
  turnOn,
  useDesktopFromPage,
  useDesktopNotifications,
  useWorkerMessages,
  type DesktopSample,
} from '../lib/desktop-notifications';
import { CallSessionProvider } from '../lib/call-session';
import { CallDock } from './CallDock';
import { VigLogo } from './Logo';
import { ShiftControl, ShiftPrompt } from './ShiftControl';
import { Button } from './ui';

interface NavItem {
  to: string;
  label: TranslationKey;
  icon: typeof Gauge;
  /** Hidden unless the user holds at least one of these. Empty = always visible. */
  permissions?: string[];
  /** Shown to department leaders as well as to whoever holds `permissions`. */
  leaders?: boolean;
  /** Shown to whoever Settings › Sites picks to publish — by name or by department — as well. */
  sitePublisher?: boolean;
  /**
   * A count beside the label — the unread mail and chats, the break requests waiting for an approver, the overtime
   * waiting for someone, the remote work waiting for a team leader's or an administrator's approval, the meeting
   * invitations waiting for an answer, the short days waiting for an administrator.
   */
  badge?: 'mail' | 'chat' | 'breakRequests' | 'overtime' | 'remoteWork' | 'meetings' | 'shortDays';
  /** Active on its own address only, not on the pages under it — Monitoring's Overview, under which the rest live. */
  end?: boolean;
}

interface NavGroup {
  label: TranslationKey | null;
  items: NavItem[];
}

/** §37.2 — the sidebar, rendered per permission. */
const NAV: NavGroup[] = [
  {
    label: null,
    items: [
      { to: '/', label: 'nav.dashboard', icon: Gauge },
      // Every employee's own mail, with whatever they have not read (operator, 2026-10-09).
      { to: '/mail', label: 'nav.mail', icon: Mail, badge: 'mail' },
      // Under Mail: chat with any other employee, a whole page like WhatsApp Web, open to
      // everyone (operator, 2026-10-19: "no roles it's like email system for every employee").
      { to: '/chat', label: 'nav.chat', icon: MessageCircle, badge: 'chat' },
      // Everyone's own sticky notes and, under them, their to-do list — two pages, which nobody else
      // reads (operator, 2026-10-15: "sprite tabs under My Notes My Tod Do List").
      { to: '/notes', label: 'nav.notes', icon: StickyNote },
      { to: '/todos', label: 'nav.todos', icon: ListTodo },
      // Under My to-do list: the meeting calendar (operator, 2026-10-21: "put it under my to do list"), with how
      // many invitations wait for an answer. Everyone has it.
      { to: '/meetings', label: 'nav.meetings', icon: CalendarCheck, badge: 'meetings' },
      // Under the meeting calendar: voice calls with any employee, every connected one recorded
      // (2026-10-29). Everyone has it; a call rings on every page, in the dock.
      { to: '/calls', label: 'nav.calls', icon: Phone },
      // Under Calls: Viganium Drive, everyone's own folders and files, shared as they choose (2026-11-02).
      { to: '/drive', label: 'nav.drive', icon: HardDrive },
      // Under Drive: websites published in folders of their own (2026-11-03) — the administrators', and
      // whoever Settings › Sites picks.
      { to: '/sites', label: 'nav.sites', icon: AppWindow, permissions: [P.SITES_MANAGE], sitePublisher: true },
    ],
  },
  {
    label: 'nav.groupClients',
    items: [{ to: '/clients', label: 'nav.clients', icon: Building2, permissions: [P.CLIENTS_VIEW] }],
  },
  {
    label: 'nav.groupWork',
    // The operator's order (2026-10-03).
    items: [
      { to: '/projects', label: 'nav.projects', icon: Briefcase },
      { to: '/tickets', label: 'nav.tickets', icon: Ticket },
      { to: '/tasks', label: 'nav.tasks', icon: ClipboardList },
      // From one department leader to another (operator, 2026-10-07): leaders and the admins.
      { to: '/leader-tasks', label: 'nav.leaderTasks', icon: Crown, leaders: true, permissions: [P.TASKS_MANAGE_ALL] },
      { to: '/q-tasks', label: 'nav.qTasks', icon: ListOrdered },
      // Everyone reads them, and anyone may post one (operator's request, 2026-10-03).
      { to: '/announcements', label: 'nav.announcements', icon: Megaphone },
      { to: '/kanban', label: 'nav.kanban', icon: Columns3 },
      // Clients' change requests from the client portal — "shown to admin and
      // team leaders, in Work, under Workflow" (operator's request, 2026-10-03).
      { to: '/client-crs', label: 'nav.clientCrs', icon: FileDiff, permissions: [P.CLIENT_CRS_MANAGE] },
      // Clients' complaints about our people (operator, 2026-10-28): every one to the
      // administrators; a department's leaders, the ones about their people.
      {
        to: '/client-complaints',
        label: 'nav.clientComplaints',
        icon: MessageSquareWarning,
        leaders: true,
        permissions: [P.CLIENT_COMPLAINTS_MANAGE],
      },
    ],
  },
  {
    label: 'nav.groupWorkforce',
    items: [
      // Shifts, Departments and Employees are the admins' and team leaders' (operator,
      // 2026-10-10). Everyone else has My schedule.
      { to: '/shifts', label: 'nav.shifts', icon: CalendarDays, permissions: [P.SHIFTS_MANAGE] },
      { to: '/my-schedule', label: 'nav.mySchedule', icon: CalendarClock },
      // Everyone starts and ends their own shift, and asks for their own leave. An approver
      // sees how many break requests wait for them (2026-10-18).
      { to: '/attendance', label: 'nav.attendance', icon: Clock, badge: 'breakRequests' },
      { to: '/leaves', label: 'nav.leaves', icon: TreePalm },
      // Overtime is only ever asked for (2026-10-21): a leader sends it, an administrator
      // approves it, the employee accepts it. Everyone sees their own, and what waits for them.
      { to: '/overtime', label: 'nav.overtime', icon: Timer, badge: 'overtime' },
      // Remote work (2026-10-28): those at the office ask for days of it; their team leader approves
      // it first, then an administrator. Everyone sees their own; the count is what waits for their approval.
      { to: '/remote-work', label: 'nav.remoteWork', icon: House, badge: 'remoteWork' },
      // §36.4 — everyone may see their own payslips. Payroll itself is run in sys.
      { to: '/my-payslips', label: 'payroll.myPayslips', icon: Wallet },
      { to: '/departments', label: 'nav.departments', icon: Building2, permissions: [P.DEPARTMENTS_READ] },
      { to: '/employees', label: 'nav.employees', icon: Users, permissions: [P.USERS_READ] },
      // An administrator sees how many days more than an hour short wait for them (CR-004).
      { to: '/violations', label: 'nav.violations', icon: ShieldAlert, badge: 'shortDays' },
      // Rewards of part of a day's pay and points added by hand — the administrators' (CR-004).
      { to: '/rewards', label: 'nav.rewards', icon: Award, permissions: [P.REWARDS_MANAGE, P.WALLET_POINTS_GRANT] },
      // Under it: AI daily points (CR-007) — the 06:00 proposals, approved or rejected one by one; the administrators'.
      { to: '/ai-points', label: 'nav.aiPoints', icon: Sparkles, permissions: [P.AI_POINTS_REVIEW, P.AI_POINTS_VIEW_ALL] },
    ],
  },
  // Monitoring — our servers, the projects on them and the URLs checked there; the
  // administrators' alone (operator, 2026-10-20: "build in section call Monitoring only admin access").
  {
    label: 'nav.groupMonitoring',
    items: [
      { to: '/monitoring', label: 'nav.monitoringOverview', icon: Activity, permissions: [P.MONITORING_MANAGE], end: true },
      { to: '/monitoring/servers', label: 'nav.monitoringServers', icon: Server, permissions: [P.MONITORING_MANAGE] },
      { to: '/monitoring/urls', label: 'nav.monitoringUrls', icon: Globe, permissions: [P.MONITORING_MANAGE] },
      { to: '/monitoring/incidents', label: 'nav.monitoringIncidents', icon: Siren, permissions: [P.MONITORING_MANAGE] },
      { to: '/monitoring/alerts', label: 'nav.monitoringAlerts', icon: BellRing, permissions: [P.MONITORING_MANAGE] },
      { to: '/monitoring/settings', label: 'nav.monitoringSettings', icon: SlidersHorizontal, permissions: [P.MONITORING_MANAGE] },
    ],
  },
  // Finance — invoices, payments, expenses, budgets, payroll — moved to sys,
  // the finance system, at its own address. The sidebar deliberately has no
  // link across to it (operator's call).
  {
    label: 'nav.groupAdmin',
    items: [
      { to: '/reports', label: 'nav.reports', icon: BarChart3, permissions: [P.REPORTS_VIEW] },
      { to: '/audit-log', label: 'nav.auditLog', icon: ScrollText, permissions: [P.AUDIT_VIEW] },
      // Everything done in the employee mobile app — the administrators' alone (operator, 2026-10-10).
      { to: '/mobile-activity', label: 'nav.mobileActivity', icon: Smartphone, permissions: [P.MOBILE_ACTIVITY_VIEW] },
      // The chat's sticker packs — the administrators' (2026-10-20); everyone sends them.
      { to: '/sticker-packs', label: 'nav.chatStickers', icon: Sticker, permissions: [P.CHAT_STICKERS_MANAGE] },
      { to: '/settings', label: 'nav.settings', icon: Settings, permissions: [P.SETTINGS_MANAGE] },
    ],
  },
];

/**
 * Calls ring, and a call goes on, whatever page someone is on (2026-10-29): the
 * call session wraps every page, and its dock sits over all of them.
 */
export function Layout({ children }: { children: ReactNode }) {
  return (
    <CallSessionProvider>
      <Shell>{children}</Shell>
      <CallDock />
    </CallSessionProvider>
  );
}

function Shell({ children }: { children: ReactNode }) {
  const { t, locale } = useI18n();
  const { user, canAny, ledDepartmentIds } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // The sidebar's count of unread mail. A new mail rings the bell too, so this
  // need not hurry: it keeps the bell's pace, and only while the tab is in view.
  const { data: mail } = useQuery({
    queryKey: ['mailbox', 'unread'],
    queryFn: () => api.get<{ count: number }>('/mailbox/unread-count'),
    refetchInterval: 30_000,
  });
  // The chats with something unread, at the same pace — and the group invitations waiting for an answer.
  // The chat page keeps it current itself.
  const { data: chat } = useQuery({
    queryKey: ['chat', 'unread'],
    queryFn: () => api.get<{ count: number; messages: number; invitations?: number }>('/chat/unread-count'),
    refetchInterval: 30_000,
  });
  const chatInvitations = chat?.invitations ?? 0;
  const breakRequests = useWaitingBreakRequests();
  const overtime = useWaitingOvertime();
  const remoteWork = useWaitingRemoteWork();
  const meetings = useWaitingMeetings();
  const shortDays = useWaitingShortDays();
  const badges: Record<NonNullable<NavItem['badge']>, number> = {
    mail: mail?.count ?? 0,
    chat: (chat?.count ?? 0) + chatInvitations,
    breakRequests,
    overtime: overtime.count,
    remoteWork: remoteWork.count,
    meetings,
    shortDays,
  };
  const badgeTitle = (badge: NonNullable<NavItem['badge']>, n: number) =>
    badge === 'mail'
      ? t('mail.unreadCount', { n })
      : badge === 'chat'
        ? chatInvitations > 0
          ? t('chat.unreadChatsInvites', { n: n - chatInvitations, m: chatInvitations })
          : t('chat.unreadChats', { n })
        : badge === 'overtime'
          ? t('overtime.waitingCount', { n })
          : badge === 'remoteWork'
            ? t('remoteWork.waitingCount', { n })
            : badge === 'meetings'
              ? t('meetings.waitingCount', { n })
              : badge === 'shortDays'
                ? t('shortfall.waitingCount', { n })
                : t('breakRequest.waitingCount', { n });
  // The chat is the whole page, edge to edge, its two panes scrolling on their own (operator, 2026-10-19).
  const fullBleed = /^\/chat(\/|$)/.test(location.pathname);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  // The language the console is in is the language this person's emails are
  // written in, so the server is told whenever it differs from what it has.
  const syncedLocale = useRef(user?.locale);
  useEffect(() => {
    if (!user || syncedLocale.current === locale) return;
    syncedLocale.current = locale;
    void api.patch('/auth/me/locale', { locale }).catch(() => {
      syncedLocale.current = undefined; // try again on the next change
    });
  }, [locale, user]);

  const visibleGroups = NAV.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) =>
        !item.permissions ||
        canAny(...item.permissions) ||
        (item.leaders === true && ledDepartmentIds.length > 0) ||
        (item.sitePublisher === true && user?.sitePublisher === true),
    ),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="flex h-full">
      {/* Sidebar — always the dark console surface, in both themes. */}
      <aside
        data-theme="dark"
        className={clsx(
          'fixed inset-y-0 start-0 z-overlay flex w-sidebar flex-col bg-ink-deep',
          'border-e border-ink-line transition-[transform,visibility] duration-base ease-precision',
          // A wide window's sidebar is part of the page, with no layer of its own: the call's windows — the dock, a
          // shared screen — float over it (2026-11-02, the operator's screenshot of a shared screen half under it).
          // Below lg it is the drawer, and opens over them.
          'lg:static lg:z-auto lg:translate-x-0',
          // The off-canvas transform is scoped to `max-lg`: `ltr:`/`rtl:` outrank the
          // `lg:` breakpoint variant, so an unscoped one would hide the desktop sidebar.
          // Closed, it is invisible too: switching the language from the top bar flips
          // it from one side to the other, and the slide would sweep across the page.
          mobileOpen
            ? 'translate-x-0'
            : 'max-lg:invisible max-lg:ltr:-translate-x-full max-lg:rtl:translate-x-full',
        )}
      >
        <div className="relative flex h-topbar items-center border-b border-ink-line px-4">
          {/* The wordmark already says “Viganium”, so the name is not repeated
              beside it — only the surface label sits underneath. */}
          <Link to="/" className="flex min-w-0 flex-1 flex-col items-center gap-1" aria-label={t('app.name')}>
            <VigLogo className="h-6 w-auto text-neutral-50" />
            <span className="block w-full truncate text-center text-mono-sm uppercase tracking-widest text-[var(--text-tertiary)]">
              {t('app.tagline')}
            </span>
          </Link>
          {/* Taken out of the flow so it cannot pull the wordmark off centre —
              it only exists below lg, where the sidebar is an overlay. */}
          <button
            type="button"
            className="absolute end-3 top-1/2 -translate-y-1/2 rounded-sm p-1 text-neutral-400 lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2.5 py-3">
          {visibleGroups.map((group, index) => (
            <div key={group.label ?? `group-${index}`} className={index > 0 ? 'mt-5' : undefined}>
              {group.label && (
                <p className="px-2.5 pb-1.5 text-overline uppercase text-[var(--text-tertiary)]">{t(group.label)}</p>
              )}
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === '/' || item.end === true}
                      className={({ isActive }) =>
                        clsx(
                          'group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-body-sm font-medium',
                          'transition-colors duration-fast ease-precision',
                          isActive
                            ? 'bg-primary/15 text-neutral-50 shadow-[inset_2px_0_0_0_#C7102E] rtl:shadow-[inset_-2px_0_0_0_#C7102E]'
                            : 'text-neutral-400 hover:bg-white/5 hover:text-neutral-100',
                        )
                      }
                    >
                      <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="truncate">{t(item.label)}</span>
                      {item.badge && badges[item.badge] > 0 && (
                        <span
                          className="ms-auto grid h-5 min-w-5 place-items-center rounded-pill bg-primary px-1.5 text-[11px] font-bold text-white tabular"
                          title={badgeTitle(item.badge, badges[item.badge])}
                        >
                          <span className="sr-only">{badgeTitle(item.badge, badges[item.badge])}</span>
                          <span aria-hidden>{badges[item.badge] > 99 ? '99+' : badges[item.badge]}</span>
                        </span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* The language moved to the top bar, and Change password and Sign out
            open from the name card (operator, 2026-10-10). */}
        <div className="border-t border-ink-line p-3">
          <AccountMenu />
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-sticky bg-ink-deep/60 lg:hidden" onClick={() => setMobileOpen(false)} aria-hidden />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onOpenMenu={() => setMobileOpen(true)} />
        {/* Full width by design: this is a dense operations console on desk
            monitors, and a centred column wasted ~700px a side at 2560. The
            gutters below are the only inset. */}
        {fullBleed ? (
          <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="shrink-0 px-4 pt-4 empty:hidden sm:px-6 lg:px-8">
              <ShiftPrompt />
              <DesktopPrompt />
            </div>
            {children}
          </main>
        ) : (
          <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
            <ShiftPrompt />
            <DesktopPrompt />
            {children}
          </main>
        )}
      </div>
    </div>
  );
}

function TopBar({ onOpenMenu }: { onOpenMenu: () => void }) {
  const { t } = useI18n();
  const [theme, setTheme] = useState<'dark' | 'light'>(
    () => (localStorage.getItem('viganium.theme') as 'dark' | 'light' | null) ?? 'dark',
  );

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('viganium.theme', theme);
  }, [theme]);

  return (
    <header data-top-bar className="sticky top-0 z-sticky flex h-topbar shrink-0 items-center gap-3 border-b hairline bg-[var(--surface-card)] px-4 sm:px-6">
      <button
        type="button"
        onClick={onOpenMenu}
        className="rounded-sm p-1.5 text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] lg:hidden"
        aria-label="Open navigation"
      >
        <Menu className="h-5 w-5" />
      </button>

      <GlobalSearch />

      <div className="ms-auto flex items-center gap-1">
        <LanguageToggle />
        <ShiftControl />
        <button
          type="button"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          className="rounded-sm p-2 text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
          aria-label={theme === 'dark' ? t('theme.toLight') : t('theme.toDark')}
        >
          {theme === 'dark' ? <Sun className="h-4.5 w-4.5" /> : <Moon className="h-4.5 w-4.5" />}
        </button>
        <NotificationsBell />
      </div>
    </header>
  );
}

/**
 * The other language, in the top bar (operator, 2026-10-10: "move change
 * languages to top"). Named in that language; only its icon on a phone.
 */
function LanguageToggle() {
  const { locale, setLocale } = useI18n();
  const other = locale === 'en' ? 'العربية' : 'English';
  return (
    <button
      type="button"
      onClick={() => setLocale(locale === 'en' ? 'ar' : 'en')}
      lang={locale === 'en' ? 'ar' : 'en'}
      aria-label={other}
      className="flex h-9 items-center gap-1.5 rounded-sm px-2 text-body-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
    >
      <Languages className="h-4 w-4" aria-hidden />
      <span className="hidden sm:inline">{other}</span>
    </button>
  );
}

function GlobalSearch() {
  const { t, localized } = useI18n();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ['search', query],
    queryFn: () => api.get<any>('/search', { q: query }),
    enabled: query.trim().length >= 2,
    staleTime: 10_000,
  });

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const go = (path: string) => {
    setOpen(false);
    setQuery('');
    navigate(path);
  };

  // CR-001 §55 — searching a company name reaches the client and its projects.
  // Its invoices and payments are searched in sys. Each group is empty for a
  // caller without the permission that owns it: the server decides, not this list.
  const groups = [
    { key: 'clients', label: t('nav.clients'), rows: data?.clients ?? [], path: (r: any) => `/clients/${r.id}`, title: (r: any) => `${r.code} — ${localized({ name: r.companyName, nameAr: r.companyNameAr })}` },
    { key: 'tickets', label: t('nav.tickets'), rows: data?.tickets ?? [], path: (r: any) => `/tickets/${r.id}`, title: (r: any) => `${r.number} — ${r.subject}` },
    { key: 'projects', label: t('nav.projects'), rows: data?.projects ?? [], path: (r: any) => `/projects/${r.id}`, title: (r: any) => `${r.code} — ${localized(r)}` },
    { key: 'tasks', label: t('nav.tasks'), rows: data?.tasks ?? [], path: (r: any) => (r.isLeaderTask ? `/leader-tasks/${r.id}` : `/tasks?highlight=${r.id}`), title: (r: any) => r.title },
    { key: 'qTasks', label: t('nav.qTasks'), rows: data?.qTasks ?? [], path: (r: any) => `/q-tasks/${r.id}`, title: (r: any) => `${r.number} — ${r.title}` },
    { key: 'announcements', label: t('nav.announcements'), rows: data?.announcements ?? [], path: (r: any) => `/announcements/${r.id}`, title: (r: any) => r.title },
  ].filter((g) => g.rows.length > 0);

  return (
    <div ref={boxRef} className="relative w-full max-w-md">
      <Search
        className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-tertiary)]"
        aria-hidden
      />
      <input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={t('common.searchPlaceholder')}
        aria-label={t('common.search')}
        className="h-9 w-full rounded-md border hairline bg-[var(--surface-sunken)] ps-9 pe-3 text-body-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] dark:placeholder:text-[var(--text-secondary)] focus:border-primary"
      />

      {open && query.trim().length >= 2 && (
        <div className="absolute inset-x-0 top-full z-dropdown mt-1.5 max-h-96 overflow-y-auto rounded-md surface shadow-lg">
          {groups.length === 0 ? (
            <p className="px-3 py-4 text-body-sm text-muted">{t('common.noResults')}</p>
          ) : (
            groups.map((group) => (
              <div key={group.key} className="border-b hairline last:border-0">
                <p className="px-3 pt-2.5 pb-1 text-overline uppercase text-[var(--text-tertiary)]">{group.label}</p>
                {group.rows.map((row: any) => (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => go(group.path(row))}
                    className="block w-full truncate px-3 py-2 text-start text-body-sm text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]"
                  >
                    {group.title(row)}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

interface Notice {
  id: string;
  title: string;
  titleAr: string | null;
  body: string | null;
  bodyAr: string | null;
  /** The record's own page (`/tasks/:id`, `/leaves/:id` …); none when the reader may not open it. */
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

interface UnreadCount {
  count: number;
  latestAt: string | null;
}

function NotificationsBell() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [sound, setSound] = useNotificationSound();
  const desktop = useDesktopNotifications(user?.id);
  const [blockedHint, setBlockedHint] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const { data: count, isFetchedAfterMount } = useQuery({
    queryKey: ['notifications', 'count'],
    queryFn: () => api.get<UnreadCount>('/notifications/unread-count'),
    refetchInterval: 30_000,
    // Keep asking while the tab is in the background: that is when the chime matters.
    refetchIntervalInBackground: true,
  });

  // A newer unread notification than the last answer is the cue to chime — and,
  // in a browser with no push service, to put it on the desktop from here.
  const latestAt = isFetchedAfterMount ? count?.latestAt : undefined;
  useNotificationChime(latestAt);
  useDesktopFromPage(latestAt, locale);

  /** A notification opened from the desktop is read, as one clicked here is. */
  const readById = (id: string) => {
    void closeShown([id]);
    void api
      .post('/notifications/mark-read', { ids: [id] })
      .catch(() => undefined)
      .finally(() => void queryClient.invalidateQueries({ queryKey: ['notifications'] }));
  };

  // The service worker: one arrived — the bell asks now; one was clicked — this page opens it.
  useWorkerMessages({
    arrived: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    opened: (id, link) => {
      if (id) readById(id);
      if (link) navigate(link);
    },
  });

  // Clicked with no page open, it opened a new one at its record, `?notice=<id>` on the address.
  const [params, setParams] = useSearchParams();
  const openedNotice = params.get('notice');
  useEffect(() => {
    if (!openedNotice) return;
    readById(openedNotice);
    const rest = new URLSearchParams(params);
    rest.delete('notice');
    setParams(rest, { replace: true });
  }, [openedNotice]);

  // Nothing unread: nothing of the bell's stays on the desktop either.
  useEffect(() => {
    if (count?.count === 0) void closeShown();
  }, [count?.count]);

  const desktopSample = (): DesktopSample => ({
    title: t('notifications.desktopSampleTitle'),
    body: t('notifications.desktopSampleBody'),
  });
  const flipDesktop = () => {
    if (desktop.state === 'blocked') setBlockedHint((v) => !v);
    else if (desktop.state === 'on') void turnOff();
    else void turnOn(desktopSample());
  };
  const desktopHint =
    desktop.state === 'blocked' && blockedHint
      ? t('notifications.desktopBlockedHint')
      : desktop.state === 'on' && desktop.mode === 'page'
        ? t('notifications.desktopPageOnly')
        : null;

  const { data: items } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => api.get<Notice[]>('/notifications'),
    enabled: open,
  });

  /**
   * Opening a notification reads it (operator, 2026-10-11: "when clicked make as
   * read"). The bell shows it read at once; the server is told, then asked again.
   */
  const markRead = (notice: Notice) => {
    if (notice.readAt) return;
    void closeShown([notice.id]);
    void queryClient.cancelQueries({ queryKey: ['notifications'] });
    const now = new Date().toISOString();
    queryClient.setQueryData<Notice[]>(['notifications', 'list'], (list) =>
      list?.map((n) => (n.id === notice.id ? { ...n, readAt: now } : n)),
    );
    queryClient.setQueryData<UnreadCount>(['notifications', 'count'], (c) => (c ? { ...c, count: Math.max(0, c.count - 1) } : c));
    void api
      .post('/notifications/mark-read', { ids: [notice.id] })
      .catch(() => undefined)
      .finally(() => void queryClient.invalidateQueries({ queryKey: ['notifications'] }));
  };

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const markAll = async () => {
    await api.post('/notifications/mark-read', {});
    void closeShown();
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const unread = count?.count ?? 0;

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-sm p-2 text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
        aria-label={`${t('notifications.title')}${unread ? ` — ${unread} ${t('notifications.unread')}` : ''}`}
      >
        <Bell className="h-4.5 w-4.5" />
        {unread > 0 && (
          <span className="absolute end-1 top-1 grid h-4 min-w-4 place-items-center rounded-pill bg-primary px-1 text-[10px] font-bold text-white tabular">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute end-0 top-full z-dropdown mt-1.5 w-80 overflow-hidden rounded-md surface shadow-lg">
          <div className="flex items-center justify-between border-b hairline px-3 py-2">
            <span className="text-title text-[var(--text-primary)]">{t('notifications.title')}</span>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <Button size="sm" variant="ghost" onClick={() => void markAll()}>
                  {t('notifications.markAllRead')}
                </Button>
              )}
              <button
                type="button"
                onClick={() => setSound(!sound)}
                aria-pressed={sound}
                aria-label={t('notifications.sound')}
                title={sound ? t('notifications.mute') : t('notifications.unmute')}
                className="rounded-sm p-1.5 text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
              >
                {sound ? <Volume2 className="h-4 w-4" aria-hidden /> : <VolumeX className="h-4 w-4" aria-hidden />}
              </button>
              {desktop.state !== 'unsupported' && (
                <button
                  type="button"
                  onClick={flipDesktop}
                  disabled={desktop.busy}
                  aria-pressed={desktop.state === 'on'}
                  aria-label={t('notifications.desktop')}
                  title={
                    desktop.state === 'on'
                      ? t('notifications.desktopTurnOff')
                      : desktop.state === 'blocked'
                        ? t('notifications.desktopBlocked')
                        : t('notifications.desktopTurnOn')
                  }
                  className="rounded-sm p-1.5 text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)] disabled:opacity-50"
                >
                  {desktop.state === 'on' ? <Monitor className="h-4 w-4" aria-hidden /> : <MonitorOff className="h-4 w-4" aria-hidden />}
                </button>
              )}
            </div>
          </div>
          {desktopHint && (
            <p className="border-b hairline bg-[var(--surface-sunken)] px-3 py-2 text-caption text-muted">{desktopHint}</p>
          )}
          <div className="max-h-96 overflow-y-auto">
            {!items?.length ? (
              <p className="px-3 py-6 text-center text-body-sm text-muted">{t('notifications.empty')}</p>
            ) : (
              items.map((n) => {
                const row = clsx(
                  'block w-full border-b hairline px-3 py-2.5 text-start last:border-0 hover:bg-[var(--surface-sunken)]',
                  !n.readAt && 'bg-primary/5',
                );
                const content = (
                  <div className="flex items-start gap-2">
                    {!n.readAt && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />}
                    <div className="min-w-0">
                      <p className="truncate text-body-sm font-medium text-[var(--text-primary)]">
                        {locale === 'ar' ? n.titleAr ?? n.title : n.title}
                      </p>
                      {(n.body || n.bodyAr) && (
                        <p className="mt-0.5 line-clamp-2 text-caption text-muted">
                          {locale === 'ar' ? n.bodyAr ?? n.body : n.body}
                        </p>
                      )}
                      <p className="mt-1 text-caption text-faint">
                        {relativeTime(n.createdAt, locale)}
                        {!n.link && ` · ${t('notifications.noPage')}`}
                      </p>
                    </div>
                  </div>
                );
                // Each one opens the record it is about. One its reader may no longer open has
                // nowhere to go: clicking it only reads it.
                return n.link ? (
                  <Link
                    key={n.id}
                    to={n.link}
                    onClick={() => {
                      markRead(n);
                      setOpen(false);
                    }}
                    className={row}
                  >
                    {content}
                  </Link>
                ) : (
                  <button key={n.id} type="button" onClick={() => markRead(n)} className={row}>
                    {content}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Offers desktop notifications in a browser that has never been asked, above the
 * page as the shift prompt is (2026-10-22). "Not now" lasts until the next sign-in;
 * the bell's switch is always there.
 */
function DesktopPrompt() {
  const { t } = useI18n();
  const { user } = useAuth();
  const desktop = useDesktopNotifications(user?.id);
  const [later, setLater] = useState(promptPutOff);

  if (!desktop.askable || later) return null;
  return (
    <div
      role="region"
      aria-label={t('notifications.desktop')}
      className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-lg border border-primary/35 bg-primary/8 px-4 py-3.5"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/15 text-primary-600 dark:text-primary-400" aria-hidden>
        <BellRing className="h-4.5 w-4.5" />
      </span>
      <div className="min-w-0 grow basis-48">
        <p className="text-title text-[var(--text-primary)]">{t('notifications.desktopPromptTitle')}</p>
        <p className="text-body-sm text-muted">{t('notifications.desktopPromptBody')}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          icon={<Monitor className="h-4 w-4" />}
          loading={desktop.busy}
          onClick={() =>
            void turnOn({ title: t('notifications.desktopSampleTitle'), body: t('notifications.desktopSampleBody') })
          }
        >
          {t('notifications.desktopPromptOn')}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            putOffPrompt();
            setLater(true);
          }}
        >
          {t('notifications.desktopPromptLater')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Who is signed in, at the foot of the sidebar. Their account's actions open
 * from it, above the card (operator, 2026-10-10: "hide change password and sign
 * out under the employee name when click on name i can show options").
 */
function AccountMenu() {
  const { t, locale } = useI18n();
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Where everyone downloads Viganium Team, the employee app (operator, 2026-10-11): the APK's link from Settings ›
  // Mobile app. Under `settings`, so saving there refreshes it; no link, no item.
  const { data: androidLink } = useQuery({
    queryKey: ['settings', 'mobile.androidDownloadUrl'],
    queryFn: () => api.get<unknown>('/settings/mobile.androidDownloadUrl'),
    staleTime: 5 * 60_000,
  });
  const android = typeof androidLink === 'string' && androidLink.trim() ? androidLink.trim() : null;

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={boxRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={clsx(
          'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-start',
          'transition-colors duration-fast ease-precision hover:bg-white/10',
          open ? 'bg-white/10' : 'bg-white/5',
        )}
      >
        <span
          className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/25 text-caption font-semibold text-primary-300"
          aria-hidden
        >
          {initials(user?.fullName ?? '?')}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-body-sm text-neutral-100">{user?.fullName}</span>
          <span className="block truncate text-caption text-[var(--text-tertiary)]">
            {user?.roles.map((r) => (locale === 'ar' ? r.nameAr ?? r.name : r.name)).join(', ')}
          </span>
        </span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute inset-x-0 bottom-full z-dropdown mb-1.5 rounded-md border border-ink-line bg-ink-card p-1.5 shadow-lg"
        >
          {android && (
            <a
              role="menuitem"
              href={android}
              target="_blank"
              rel="noopener noreferrer"
              title={t('auth.androidAppHint')}
              onClick={() => setOpen(false)}
              className={ACCOUNT_ITEM}
            >
              <Smartphone className="h-4 w-4 shrink-0" aria-hidden />
              <span className="min-w-0 flex-1">{t('auth.androidApp')}</span>
              <Download className="h-3.5 w-3.5 shrink-0 text-neutral-500" aria-hidden />
            </a>
          )}
          {/* Everyone's own points wallet, under Android app and above Change password (operator, 2026-10-20). */}
          <Link role="menuitem" to="/wallet" onClick={() => setOpen(false)} className={ACCOUNT_ITEM}>
            <Coins className="h-4 w-4 shrink-0" aria-hidden />
            {t('wallet.menu')}
          </Link>
          <Link role="menuitem" to="/account/password" onClick={() => setOpen(false)} className={ACCOUNT_ITEM}>
            <KeyRound className="h-4 w-4 shrink-0" aria-hidden />
            {t('auth.changePassword')}
          </Link>
          <button role="menuitem" type="button" onClick={() => void signOut()} className={ACCOUNT_ITEM}>
            <LogOut className="h-4 w-4 shrink-0 flip-rtl" aria-hidden />
            {t('auth.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}

const ACCOUNT_ITEM =
  'flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-start text-body-sm text-neutral-300 transition-colors duration-fast hover:bg-white/5 hover:text-neutral-50';

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
